import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetCreditUtilizationsApi } from './use-get-credit-utilizations-api';

const endpoint = 'ums/v1/cms/bnpl/credit-utilizations';

type TData = Record<string, any>;
type TError = AxiosError;
type TVariables = { utilizationId: number; body: { notes?: string } };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useApproveCreditUtilizationApi(options?: Options): ReturnType {
  const { refetch: refetchUtilizations } = useGetCreditUtilizationsApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ utilizationId, body }: TVariables) => {
    try {
      const { data } = await http.patch(`${endpoint}/${utilizationId}/approve`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    ...options,
    mutationKey: [`${endpoint}/approve`],
    onSuccess: () => {
      refetchUtilizations();
      toast({ description: 'Credit utilization approved successfully', status: 'success' });
    },
  });
}
